import React from 'react';

// =============================================
// FI ADVISOR CHAT COMPONENT
// =============================================
export function FIAdvisorChat({ report, generateFIReport }) {
    const [messages, setMessages] = React.useState([
        { role: 'ai', text: 'Your live financial profile is loaded — accounts, loans, budgets and gold. Ask me anything about your finances.' }
    ]);
    const [input, setInput] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [apiKey, setApiKey] = React.useState(() => localStorage.getItem('fi_claude_api_key') || '');
    const [showKeyInput, setShowKeyInput] = React.useState(false);
    const chatRef = React.useRef(null);

    React.useEffect(() => {
        if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }, [messages]);

    const buildProfile = () => {
        const r = report;
        const sym = r.baseCurrency === 'LKR' ? '₨' : r.baseCurrency === 'USD' ? '$' : 'S$';
        const f = n => sym + Math.round(n || 0).toLocaleString();
        const loans = (r.personalLoans?.loans || []).map((l, i) =>
            `LOAN-${String(i + 1).padStart(2, '0')}: ${l.type}, ${f(l.principalRemainingBase)} remaining, ${l.monthsLeft}m left, ${f(l.monthlyInstallment)}/mo, ends ${l.settlementDate || 'n/a'}`
        ).join('\n');
        const budgetLines = [...(r.budgets?.sgCycle1 || []), ...(r.budgets?.sgCycle2 || [])]
            .filter(i => i.enabled && i.amountBase > 0)
            .map(i => `  ${i.category}: budget ${f(i.amountBase)}, spent ${f(i.actualSpentBase)}`)
            .join('\n');
        return `CLIENT FINANCIAL PROFILE (iPlanner live data, ${new Date().toDateString()})
Base currency: ${r.baseCurrency} | LKR rate: ${r.exchangeRates?.lkrPerSgd} | Gold price: S$${r.exchangeRates?.goldPriceSgdPerGram22K}/g (22K)

SUMMARY: Cash ${f(r.summary?.totalCashAndBankBalance)}, Gold ${f(r.summary?.totalGoldValue)}, Loans outstanding ${f(r.summary?.totalPersonalLoanRemaining)}, Monthly loan payments ${f(r.summary?.totalPersonalLoanMonthlyPayment)}, CC ${f(r.summary?.totalCreditCardOutstanding)}, Net worth ${f(r.summary?.netWorthEstimate)}

LOANS:\n${loans || 'None'}

BUDGET THIS CYCLE:\n${budgetLines || 'No data'}

GOLD: ${(r.goldInventory?.byTypeAndQuality || []).map(g => `${g.count}x ${g.type} (${g.quality}), ${g.totalWeightG}g, ${f(g.totalValueBase)}`).join('; ') || 'None'}`;
    };

    const sendChat = async (question) => {
        const q = (question || input).trim();
        if (!q) return;
        if (!apiKey) { setShowKeyInput(true); return; }
        setInput('');
        setLoading(true);
        setMessages(prev => [...prev, { role: 'user', text: q }, { role: 'ai', text: '…' }]);
        try {
            const res = await fetch('https://api.anthropic.com/v1/messages', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey, 'anthropic-version': '2023-06-01' },
                body: JSON.stringify({
                    model: 'claude-sonnet-4-20250514',
                    max_tokens: 1000,
                    system: `You are a personal financial advisor with full access to this client's iPlanner data. Give direct, specific, number-based advice in 4–6 sentences max. Always reference actual loan amounts and dates when relevant. Never give generic advice.\n\n${buildProfile()}`,
                    messages: [{ role: 'user', content: q }]
                })
            });
            const data = await res.json();
            const reply = data.content?.[0]?.text || (data.error?.message ? `API error: ${data.error.message}` : 'No response.');
            setMessages(prev => [...prev.slice(0, -1), { role: 'ai', text: reply }]);
        } catch (e) {
            setMessages(prev => [...prev.slice(0, -1), { role: 'ai', text: 'Connection error — check your API key and network.' }]);
        }
        setLoading(false);
    };

    const quickQs = [
        'What should I prioritize financially right now?',
        'How tight is my monthly cash flow?',
        'Which loan should I pay off first?',
        'Should I settle any loans early?',
    ];

    return (
        <div>
            {/* API Key setup */}
            {(showKeyInput || !apiKey) && (
                <div style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: '12px', padding: '12px 14px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#c4b5fd', marginBottom: '8px', fontWeight: '600' }}>🔑 Claude API Key required for in-app chat</div>
                    <div style={{ fontSize: '11px', color: '#a78bfa', marginBottom: '8px', lineHeight: '1.5' }}>Get a key at <a href="https://console.anthropic.com" target="_blank" style={{ color: '#c4b5fd' }}>console.anthropic.com</a>. Stored locally in your browser only.</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                            type="password"
                            placeholder="sk-ant-..."
                            value={apiKey}
                            onChange={e => setApiKey(e.target.value)}
                            style={{ flex: 1, fontSize: '12px', padding: '7px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: '8px', color: '#f8fafc', outline: 'none', fontFamily: 'inherit' }}
                        />
                        <button onClick={() => { localStorage.setItem('fi_claude_api_key', apiKey); setShowKeyInput(false); }}
                            style={{ fontSize: '12px', padding: '7px 14px', background: '#7c3aed', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' }}>
                            Save
                        </button>
                    </div>
                </div>
            )}

            {/* Quick prompts */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                {quickQs.map((q, i) => (
                    <button key={i} onClick={() => sendChat(q)} disabled={loading}
                        style={{ fontSize: '11px', padding: '5px 10px', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '20px', color: '#a5b4fc', cursor: 'pointer', fontFamily: 'inherit' }}>
                        {q}
                    </button>
                ))}
                {apiKey && (
                    <button onClick={() => setShowKeyInput(v => !v)}
                        style={{ fontSize: '11px', padding: '5px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', color: 'var(--text-tertiary)', cursor: 'pointer', fontFamily: 'inherit' }}>
                        🔑 API Key
                    </button>
                )}
            </div>

            {/* Chat window */}
            <div ref={chatRef} style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '340px', overflowY: 'auto', marginBottom: '10px', padding: '4px 0' }}>
                {messages.map((m, i) => (
                    <div key={i} style={{
                        padding: '10px 14px', borderRadius: '12px', fontSize: '13px', lineHeight: '1.6',
                        maxWidth: '92%', alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                        background: m.role === 'user' ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.05)',
                        color: m.role === 'user' ? '#a5b4fc' : 'var(--text-primary)',
                        border: m.role === 'user' ? '1px solid rgba(99,102,241,0.3)' : '1px solid rgba(255,255,255,0.07)',
                        whiteSpace: 'pre-wrap',
                    }}>
                        {m.text}
                    </div>
                ))}
            </div>

            {/* Input row */}
            <div style={{ display: 'flex', gap: '8px' }}>
                <input
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && sendChat()}
                    placeholder="Ask about your finances..."
                    disabled={loading}
                    style={{ flex: 1, fontSize: '13px', padding: '10px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit' }}
                />
                <button onClick={() => sendChat()} disabled={loading || !input.trim()}
                    style={{ padding: '10px 16px', background: loading ? 'rgba(124,58,237,0.4)' : '#7c3aed', border: 'none', borderRadius: '10px', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontWeight: '600', fontSize: '13px' }}>
                    {loading ? '…' : '↗'}
                </button>
            </div>
        </div>
    );
}